import { passwordRegex } from "constants/regex";
import { FormControl, IconButton, InputAdornment, InputLabel, OutlinedInput, Typography, useTheme, styled } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { InitPageContainer } from "components/views/InitPageContainer/InitPageContainer";
import { RoutePaths } from "constants/RoutePaths";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Stack } from "@mui/system";
import LoadingButton from "@mui/lab/LoadingButton";
import { useLocation, useNavigate } from "react-router-dom";
import { useResetPassword } from "hooks/useResetPassword";
import { useVerifyResetPassword } from "hooks/useVerifyResetPassword";
import { StyledErrorDiv } from "@theme/styledComponents";

export const ResetPasswordPage = () => {
    const [password, setPassword] = useState('');
    const [passwordRepeated, setPasswordRepeated] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [passwordRepeatedError, setPasswordRepeatedError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordRepeated, setShowPasswordRepeated] = useState(false);
    const location = useLocation();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const { resetPassword, resetPasswordPending } = useResetPassword();
    const { fetchVerifyResetPassword } = useVerifyResetPassword();

    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');
    const email = queryParams.get('email');

    const isValidPassword = (password: string): boolean => {
        return passwordRegex.test(password);
    };

    const validatePassword = () => {
        if (isValidPassword(password) === false) {
            setPasswordError(t('finishRegistrationPage.invalidPassword'));
            return false;
          }
          setPasswordError('');
          return true;
    }

    const validatePasswordRepeated = () => {
        if (isValidPassword(passwordRepeated) === false) {
            setPasswordRepeatedError(t('finishRegistrationPage.invalidPassword'));
            return false;
        }

        if (passwordRepeated !== password) {
            setPasswordRepeatedError(t('finishRegistrationPage.passwordsDoNotMatch'));
            return false;
        }

        setPasswordRepeatedError('');
        return true;
    }

    const handleClickShowPassword = () => setShowPassword(!showPassword);
    const handleMouseDownPassword = (event: any) => event.preventDefault();

    const handleClickShowPasswordRepeated = () => setShowPasswordRepeated(!showPasswordRepeated);
    const handleMouseDownPasswordRepeated = (event: any) => event.preventDefault();

    const onSubmit = async () => {
        const isPasswordValid = validatePassword();
        const isPasswordRepeatedValid = validatePasswordRepeated();

        if (isPasswordValid && isPasswordRepeatedValid && !!email && !!token) {
          const resp = await resetPassword({ email, token: encodeURIComponent(token), newPassword: password });
          if (resp.success) {
            navigate(RoutePaths.LOGIN);
          }
        }
    }

    useEffect(() => {
      const checkResetLinkStatus = async (email: string, token: string) => {
        const resp = await fetchVerifyResetPassword(email, encodeURIComponent(token));
        if (resp && resp.data) {
          if (resp.data.isTokenValid !== true) {
            navigate(RoutePaths.RESET_PASSWORD_ERROR);
          }
        }
      }

      if (email && token) {
        checkResetLinkStatus(email, token);
      }
    }, [email, token, fetchVerifyResetPassword]);

    return (
      <InitPageContainer cardHeight="40%" title={t('resetPasswordPage.createNewPassword')} footerText={t('resetPasswordPage.backToLogin')} footerUrl={RoutePaths.LOGIN}>
        <Stack spacing={theme.spacing(4)}>
            <FormControl variant="outlined">
              <InputLabel htmlFor="password">{t('general.password')}</InputLabel>
              <OutlinedInput
                id="password"
                label={t('general.password')}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={validatePassword}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              {passwordError && <StyledErrorDiv>{passwordError}</StyledErrorDiv>}
              </FormControl>
              <FormControl variant="outlined">
              <InputLabel htmlFor="passwordRepeated">{t('finishRegistratioNpage.passwordRepeat')}</InputLabel>
              <OutlinedInput
                id="passwordRepeated"
                label={t('finishRegistratioNpage.passwordRepeat')}
                type={showPasswordRepeated ? 'text' : 'password'}
                value={passwordRepeated}
                onChange={(e) => setPasswordRepeated(e.target.value)}
                onBlur={validatePasswordRepeated}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowPasswordRepeated} onMouseDown={handleMouseDownPasswordRepeated} edge="end">
                      {showPasswordRepeated ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              </FormControl>
              {passwordRepeatedError && <StyledErrorDiv>{passwordRepeatedError}</StyledErrorDiv>}
              <Typography variant="body2">{t('finishRegistrationPage.passwordRequirements')}</Typography>              
              <LoadingButton disabled={!!passwordError || !!passwordRepeatedError || !password || !passwordRepeated} type='submit' loading={resetPasswordPending} variant='contained' color='primary' onClick={onSubmit}>{t('finishRegistrationPage.changePassword')}</LoadingButton>
            </Stack>
        </InitPageContainer>
    );
  };